import { useState, useEffect } from "react";
import { fetchConversationThread } from "./api";
import type { ConversationThread as ConversationThreadType } from "./api";

interface ConversationThreadProps {
  conversationId: string;
}

export function ConversationThread({ conversationId }: ConversationThreadProps) {
  const [thread, setThread] = useState<ConversationThreadType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchConversationThread(conversationId)
      .then(setThread)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [conversationId]);

  if (loading) {
    return (
      <div className="cx-admin-thread">
        <p className="cx-text-secondary" style={{ fontSize: "var(--cx-text-xs)" }}>
          Loading conversation...
        </p>
      </div>
    );
  }

  if (error || !thread || thread.messages.length === 0) {
    return (
      <div className="cx-admin-thread">
        <p className="cx-text-secondary" style={{ fontSize: "var(--cx-text-xs)" }}>
          {error ? "Failed to load conversation" : "No messages"}
        </p>
      </div>
    );
  }

  return (
    <div className="cx-admin-thread">
      {thread.messages.map((msg, i) => (
        <div key={i}>
          <div
            className={`cx-admin-thread-msg ${
              msg.role === "user" ? "cx-admin-thread-msg--user" : "cx-admin-thread-msg--assistant"
            }`}
          >
            <div className="cx-admin-thread-role">{msg.role}</div>
            {msg.role === "user"
              ? msg.content
              : msg.content.length > 300
                ? msg.content.slice(0, 300) + "..."
                : msg.content}
          </div>
          {msg.tools.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
              {msg.tools.map((tool, j) => (
                <span
                  key={j}
                  className={`cx-admin-thread-tool ${tool.is_error ? "cx-admin-thread-tool--error" : ""}`}
                >
                  {tool.name ?? "tool"}
                  {tool.result && (
                    <span className="cx-admin-thread-tool-result">
                      {" "}
                      &rarr; {tool.result.length > 60 ? tool.result.slice(0, 60) + "..." : tool.result}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      {thread.truncated && (
        <p
          style={{
            fontSize: "var(--cx-text-xs)",
            color: "var(--color-text-placeholder)",
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          {thread.total_messages - thread.messages.length} more messages...
        </p>
      )}
    </div>
  );
}
